// Every frame on a FrameX sheet carries its clipped image, applied on sight.
// The canvas creation paths in Document.svelte are inconsistent about it: a
// dragged box attaches the image before insert, a click places a default frame
// with none, and a 5-24px drag places one with no clip at all. FrameX cannot
// intercept the gesture — those handlers are Document.svelte's — so it holds
// the invariant on the result instead.

import { get } from "svelte/store";
import { locationDrawingUpdate } from "#src/extensions/docs/location-drawing.js";
import { isRecordImaged } from "./derive.js";

export function createFrameImageTracker() {
  let running = false;
  let pending = null;
  const failed = new Set();
  const repairCounts = new Map();

  return async function ensure(frames, group) {
    // A pass arriving mid-repair is remembered, not dropped. The in-flight
    // repair normally ends in a group.update whose store write triggers a fresh
    // pass, but a repair where every candidate failed writes nothing — and a
    // frame drawn during it would sit unimaged with no retry.
    if (running) {
      pending = { frames, group };
      return 0;
    }

    const grp = get(group);
    // locationDrawingUpdate is a TOGGLE: handed a frame that already has its
    // image it REMOVES it. Only ever pass frames confirmed to be missing one.
    // Missing is RECORD-scoped, never placement-scoped: an item has a single
    // drawing attachment, so with two placements (a pasted frame) a
    // placement-scoped test always finds one "missing", and repairing it
    // un-images the sibling — the repair ping-pongs forever and OOMs the tab.
    const missing = frames.filter(
      (l) => l.clip && !failed.has(l.id) && !isRecordImaged(l, grp),
    );
    if (!missing.length) return 0;

    running = true;
    try {
      const results = await Promise.all(
        missing.map((l) => locationDrawingUpdate(l.id, get(group))),
      );
      const updates = [];
      results.forEach((u, i) => {
        // No usable image: unresolvable records (null), or a failed build on an
        // image sheet, which returns `value: undefined` and would blank the
        // item's drawing if applied. Parked so it cannot re-run every pass.
        if (u && u.value) {
          const id = missing[i].id;
          const n = (repairCounts.get(id) ?? 0) + 1;
          repairCounts.set(id, n);
          if (n > 2) {
            // A placement needing a third repair means an invariant
            // disagreement — park it rather than loop (2026-11-02 paste OOM).
            console.warn("frame image repair oscillating; parking", id);
            failed.add(id);
          } else {
            updates.push(u);
          }
        } else {
          failed.add(missing[i].id);
        }
      });
      if (updates.length) await group.update(updates);
      return updates.length;
    } finally {
      running = false;
      const next = pending;
      pending = null;
      if (next) await ensure(next.frames, next.group);
    }
  };
}
